/**
 * JOServicesSpotlight — Full-width feature strip that highlights the
 * two key services (Entretien de jardin + Aménagement extérieur) and
 * pushes the visitor to /services or the quote request form.
 *
 * Rendered on the Jardinerie home just above products, and injected at
 * the very top of /shop via a companion DOM injector.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Scissors, TreePine, ArrowRight, PhoneCall } from "lucide-react";
import { useSettings } from "@/lib/useSettings";

export default function JOServicesSpotlight({ compact = false }) {
  const { settings: s } = useSettings();
  const phone = s?.universe_paysagiste?.phone || s?.phone || "";

  return (
    <section
      className={`jo-services-spotlight ${compact ? "jo-services-spotlight--compact" : ""}`}
      data-testid="jo-services-spotlight"
    >
      <div className="jo-container jo-services-spotlight-inner">
        <div className="jo-services-spotlight-head">
          <span className="jo-services-spotlight-tag">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={2.4} />
            Notre coeur de métier
          </span>
          <h2 className="jo-services-spotlight-title">
            Vos jardins et espaces extérieurs, <em>entretenus et transformés</em> par nos paysagistes
          </h2>
          <p className="jo-services-spotlight-lead">
            De la taille saisonnière à la création complète de jardin, nos équipes se déplacent sur toute la région d&apos;Istres pour prendre soin de vos extérieurs.
          </p>
        </div>

        <div className="jo-services-spotlight-grid">
          <Link to="/services?type=entretien" className="jo-services-spotlight-card jo-services-spotlight-card--entretien" data-testid="spotlight-card-entretien">
            <div className="jo-services-spotlight-icon">
              <Scissors className="w-6 h-6" strokeWidth={2} />
            </div>
            <div className="jo-services-spotlight-card-title">Entretien de jardin</div>
            <ul className="jo-services-spotlight-list">
              <li>Tonte, taille &amp; désherbage</li>
              <li>Élagage &amp; abattage</li>
              <li>Contrats annuels sur mesure</li>
            </ul>
            <span className="jo-services-spotlight-cta">
              Découvrir <ArrowRight className="w-4 h-4" />
            </span>
          </Link>

          <Link to="/services?type=amenagement" className="jo-services-spotlight-card jo-services-spotlight-card--amenagement" data-testid="spotlight-card-amenagement">
            <div className="jo-services-spotlight-icon">
              <TreePine className="w-6 h-6" strokeWidth={2} />
            </div>
            <div className="jo-services-spotlight-card-title">Aménagement extérieur</div>
            <ul className="jo-services-spotlight-list">
              <li>Création de jardins clé en main</li>
              <li>Terrasses, allées, éclairage</li>
              <li>Arrosage automatique &amp; plantations</li>
            </ul>
            <span className="jo-services-spotlight-cta">
              Nos réalisations <ArrowRight className="w-4 h-4" />
            </span>
          </Link>
        </div>

        <div className="jo-services-spotlight-footer">
          <Link to="/contact?type=devis-services" className="jo-services-spotlight-btn-primary" data-testid="spotlight-btn-devis">
            Demander un devis gratuit
            <ArrowRight className="w-4 h-4" strokeWidth={2.4} />
          </Link>
          {phone && (
            <a href={`tel:${phone.replace(/\s/g, "")}`} className="jo-services-spotlight-btn-phone" data-testid="spotlight-btn-phone">
              <PhoneCall className="w-4 h-4" strokeWidth={2.4} />
              <span>{phone}</span>
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
